// src/features/workflows/LinkedWorkflowRunsIndicator.jsx
//
// WORKFLOWS-SLICE-2-LAUNCH-FROM-OBJECT: source→run reverse indicator.
// Small badge shown on an originating object (Decision/Location/Lease/
// Plan/Signal) view when one or more workflow runs were launched from it.
// Clicking it opens a popover listing every linked run as
// "Workflow: <name> · <status>", each independently clickable so any of
// the linked runs (not just the most recent) can be opened.
// Fetches via GET /api/workflows/runs/linked?sourceType=&sourceId=.

import React, { useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { getLinkedWorkflowRuns } from "../../data/workflowsClient.js";
import { log } from "../../utils/logger.js";

export function LinkedWorkflowRunsIndicator({ sourceType, sourceId, onOpenRun, style }) {
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!sourceType || !sourceId) {
      setRuns([]);
      return undefined;
    }
    getLinkedWorkflowRuns(sourceType, sourceId)
      .then((list) => { if (!cancelled) setRuns(list); })
      .catch((err) => {
        log.error("LinkedWorkflowRunsIndicator: fetch failed", { sourceType, sourceId, err: String(err) });
        if (!cancelled) setRuns([]);
      });
    return () => { cancelled = true; };
  }, [sourceType, sourceId]);

  useEffect(() => {
    if (!open) return undefined;
    function handleClickAway(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [open]);

  if (runs.length === 0) return null;

  const label = runs.length === 1 ? "1 action started from here" : `${runs.length} actions started from here`;

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        data-testid="linked-workflow-runs-indicator"
        onClick={() => {
          if (runs.length === 1) {
            onOpenRun?.(runs[0].id);
          } else {
            setOpen((prev) => !prev);
          }
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: `1px solid ${T.border}`,
          borderRadius: 999,
          padding: "3px 10px",
          fontSize: T.fsXs,
          fontWeight: T.fwSemibold,
          color: "var(--v2-accent)",
          cursor: runs.length > 1 || onOpenRun ? "pointer" : "default",
          ...style,
        }}
      >
        {label}
      </button>

      {open && runs.length > 1 && (
        <div
          data-testid="linked-workflow-runs-popover"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            background: T.white,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            minWidth: 220,
            padding: 4,
          }}
        >
          {runs.map((run) => (
            <button
              key={run.id}
              type="button"
              data-testid={`linked-workflow-run-${run.id}`}
              onClick={() => {
                setOpen(false);
                onOpenRun?.(run.id);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                padding: "6px 8px",
                fontSize: T.fsXs,
                fontWeight: T.fwSemibold,
                color: "var(--v2-dark)",
                cursor: "pointer",
              }}
            >
              {`Action: ${run.name} · ${run.status}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
